import { useEffect, useState, useCallback, useRef } from 'react';
import { FiX, FiAlertCircle, FiAlertTriangle, FiInfo, FiBell } from 'react-icons/fi';
import { cn } from '@/lib/utils';

export interface ToastData {
  id: string;
  title: string;
  message?: string;
  severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  duration?: number;
}

interface ToastProps {
  toasts: ToastData[];
  onClose: (id: string) => void;
}

function getSeverityIcon(severity?: string) {
  switch (severity) {
    case 'CRITICAL': return <FiAlertCircle />;
    case 'HIGH': return <FiAlertTriangle />;
    case 'MEDIUM': return <FiInfo />;
    case 'LOW': return <FiBell />;
    default: return <FiBell />;
  }
}

function getSeverityColor(severity?: string) {
  switch (severity) {
    case 'CRITICAL': return 'border-danger border-l-danger';
    case 'HIGH': return 'border-warning border-l-warning';
    case 'MEDIUM': return 'border-yellow-500 border-l-yellow-500';
    case 'LOW': return 'border-success border-l-success';
    default: return 'border-border border-l-border';
  }
}

function getSeverityIconColor(severity?: string) {
  switch (severity) {
    case 'CRITICAL': return 'text-danger';
    case 'HIGH': return 'text-warning';
    case 'MEDIUM': return 'text-yellow-500';
    case 'LOW': return 'text-success';
    default: return 'text-text-secondary';
  }
}

export default function Toast({ toasts, onClose }: ToastProps) {
  const [exitingToasts, setExitingToasts] = useState<Set<string>>(new Set());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const handleClose = useCallback((id: string) => {
    setExitingToasts(prev => new Set(prev).add(id));
    setTimeout(() => {
      onClose(id);
      setExitingToasts(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  }, [onClose]);

  useEffect(() => {
    toasts.forEach(toast => {
      if (!timersRef.current.has(toast.id)) {
        const duration = toast.duration ?? 5000;
        const timer = setTimeout(() => {
          handleClose(toast.id);
          timersRef.current.delete(toast.id);
        }, duration);
        timersRef.current.set(toast.id, timer);
      }
    });

    const currentIds = new Set(toasts.map(t => t.id));
    timersRef.current.forEach((timer, id) => {
      if (!currentIds.has(id)) {
        clearTimeout(timer);
        timersRef.current.delete(id);
      }
    });
  }, [toasts, handleClose]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={cn(
            'bg-background-secondary border border-l-4 rounded-lg p-4 min-w-[320px] max-w-[400px] shadow-[0_4px_12px_rgba(0,0,0,0.4)] pointer-events-auto',
            getSeverityColor(toast.severity),
            exitingToasts.has(toast.id)
              ? 'animate-slide-out-right'
              : 'animate-slide-in-right'
          )}
        >
          <div className="flex items-start gap-3 mb-2">
            <div className={cn('text-xl flex-shrink-0 mt-0.5', getSeverityIconColor(toast.severity))}>
              {getSeverityIcon(toast.severity)}
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-text-primary mb-1">{toast.title}</div>
              {toast.message && (
                <div className="text-[13px] text-text-secondary leading-relaxed">{toast.message}</div>
              )}
            </div>
            <button
              onClick={() => handleClose(toast.id)}
              className="bg-transparent border-none text-text-secondary cursor-pointer p-0 flex-shrink-0 hover:text-text-primary [&_svg]:w-4 [&_svg]:h-4"
            >
              <FiX />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
